import type { Pouch, Sheet, Group } from '../types';
import { POUCH_COLORS } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { updateSheet } from './SheetLogic';
import { clonePouchLayerValues, resolveActiveLayerId, setPouchLayerValue } from './LayerLogic';
import React from 'react';

// Z-indexの定数定義
const MIN_POUCH_Z_INDEX = 200001;
const MAX_POUCH_Z_INDEX = 299999;

/**
 * パウチを作成する
 * @param x パウチのX座標（中心点）
 * @param y パウチのY座標（中心点）
 * @param defaultValue パウチのデフォルト値
 * @param maxZIndex 現在の最大Z-Index
 * @param sheet 現在のシート（最後の値とカラーを取得するため）
 * @returns 作成したパウチオブジェクト
 */
export const createPouch = (
  x: number, 
  y: number, 
  defaultValue: number, 
  defaultColor: string,
  maxZIndex: number,
  sheet?: Sheet
): Pouch => {
  // 新しいzIndexを計算（範囲内に収める）
  const newZIndex = Math.max(MIN_POUCH_Z_INDEX, Math.min(maxZIndex + 1, MAX_POUCH_Z_INDEX));
  
  // シートから最後の値とカラーを取得、なければデフォルト値を使用
  const value = sheet?.lastPouchValue ?? defaultValue;
  const color = defaultColor;
  
  const pouch: Pouch = {
    id: uuidv4(),
    value,
    multiplier: 1,
    x,
    y,
    isNegative: value < 0,
    zIndex: newZIndex,
    color,
    _animateFromZero: true
  };
  if (sheet?.layers?.length) {
    const lid = resolveActiveLayerId(sheet);
    return setPouchLayerValue(pouch, lid, value, undefined, sheet);
  }
  return pouch;
};

/**
 * パウチを更新する
 * @param pouchId 更新するパウチのID
 * @param updates 更新内容（Pouchの部分オブジェクト）
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const updatePouch = async (
  pouchId: string, 
  updates: Partial<Pouch>, 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  const updatedPouches = activeSheet.pouches.map(p => 
    p.id === pouchId ? { ...p, ...updates } : p
  );
  
  const updatedSheet = {
    ...activeSheet,
    pouches: updatedPouches,
    lastModified: new Date(),
    // カラーが更新された場合はlastPouchColorを更新
    ...(updates.color !== undefined && { lastPouchColor: updates.color })
  };
  
  await updateSheet(updatedSheet, dispatch);
};

/**
 * パウチの値を更新する
 * @param pouchId 更新するパウチのID
 * @param value 新しい値
 * @param isNegative 負の値かどうか
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const updatePouchValue = async (
  pouchId: string, 
  value: number, 
  isNegative: boolean, 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  await updatePouch(
    pouchId, 
    { 
      value,
      isNegative,
      isTime: false, // PouchLogicではisTimeの判定は行わず、数式処理で設定された値を使用
      _animateSignChange: activeSheet.pouches.find(p => p.id === pouchId)?.isNegative !== isNegative
    }, 
    activeSheet, 
    dispatch
  );
};

/**
 * パウチの乗数を更新する
 * @param pouchId 更新するパウチのID
 * @param multiplier 新しい乗数
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const updatePouchMultiplier = async (
  pouchId: string, 
  multiplier: number, 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  await updatePouch(
    pouchId, 
    { 
      multiplier,
      _animateMultiplier: true
    }, 
    activeSheet, 
    dispatch
  );
};

/**
 * パウチの位置を更新する
 * @param pouchId 更新するパウチのID
 * @param x 新しいX座標
 * @param y 新しいY座標
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const updatePouchPosition = async (
  pouchId: string, 
  x: number, 
  y: number, 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  await updatePouch(pouchId, { x, y }, activeSheet, dispatch);
};

/**
 * パウチの色を更新するhttps://davidgarciasantes.medium.com/remove-all-console-log-using-regex-in-vscode-9fd76476fa0f
 * @param pouchId 更新するパウチのID
 * @param color 新しい色コード
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const updatePouchColor = async (
  pouchId: string, 
  color: string, 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  await updatePouch(pouchId, { color }, activeSheet, dispatch);
};

/**
 * パウチを削除する
 * @param pouchId 削除するパウチのID
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 * @param onUpdateActiveSheet 統合更新API関数（オプション）
 */
export const deletePouch = async (
  pouchId: string, 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>,
  onUpdateActiveSheet?: (pouches: Pouch[] | null, groups: Group[] | null, isCritical?: boolean, saveToDb?: boolean) => void
): Promise<void> => {
  // 削除アニメーションのためにフラグを設定
  const updatedPouches = activeSheet.pouches.map(p => 
    p.id === pouchId ? { ...p, _isDeleting: true } : p
  );
  
  // 統合更新APIがある場合は、それを使用
  if (onUpdateActiveSheet) {
    
    // 現在のグループ状態を取得
    const currentGroups = activeSheet.groups;
    // 統合更新APIを呼び出し（パウチの削除フラグをセットして保存）
    onUpdateActiveSheet(updatedPouches, currentGroups, true);
    return;
  }
  
};

/**
 * 複数のパウチを更新する
 * @param pouches 更新後のパウチの配列
 * @param activeSheet 現在のアクティブシート
 * @param dispatch Reducerのdispatch関数
 */
export const updatePouches = async (
  pouches: Pouch[], 
  activeSheet: Sheet,
  dispatch: React.Dispatch<any>
): Promise<void> => {
  const movedPouches = pouches.filter(p => (p as any)._isMoving);
    JSON.stringify(movedPouches.map(p => ({
      id: p.id, 
      x: p.x, 
      y: p.y, 
      _isMoving: (p as any)._isMoving
    })), null, 2);
  
  const updatedSheet = {
    ...activeSheet,
    pouches,
    lastModified: new Date()
  };
  
  
  await updateSheet(updatedSheet, dispatch);
  
};

/**
 * パウチを複製する
 * @param pouch 複製元のパウチ
 * @param x 新しいX座標
 * @param y 新しいY座標
 * @param maxZIndex 現在の最大Z-index
 * @returns 複製されたパウチ
 */
/** 座標・id 以外のパウチデータをコピー */
export function copyPouchDataFields(source: Pouch) {
  return {
    value: source.value,
    multiplier: source.multiplier,
    isNegative: source.isNegative,
    color: source.color,
    title: source.title,
    isGroupTotalPouch: source.isGroupTotalPouch,
    referencedGroupId: source.referencedGroupId,
    oppositeToTotal: source.oppositeToTotal,
    isTime: source.isTime,
    lastExpression: source.lastExpression,
    valueWhenLastExpression: source.valueWhenLastExpression,
    multiplyWhenLastExpression: source.multiplyWhenLastExpression,
    roundUnit: source.roundUnit,
    roundUnitWhenLastExpression: source.roundUnitWhenLastExpression,
    layerValues: clonePouchLayerValues(source.layerValues),
  };
}

/** シート複製など：座標を保ったまま新 id で複製 */
export function duplicatePouchRecord(source: Pouch, newId: string): Pouch {
  return {
    id: newId,
    ...copyPouchDataFields(source),
    x: source.x,
    y: source.y,
    zIndex: source.zIndex,
  };
}

export const clonePouch = (pouch: Pouch, x: number, y: number, maxZIndex: number): Pouch => {
  return {
    id: uuidv4(),
    ...copyPouchDataFields(pouch),
    x,
    y,
    zIndex: maxZIndex + 1,
    _animateFromZero: true,
  };
}; 